import { useEffect, useState } from "react";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { apiRequest } from "@lato/client/lib/apiClient";
import { useMe } from "@/hooks/useCloudChat";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

type Status = "linking" | "success" | "error";

function SlackLink() {
  const { state, team_id, slack_user_id } = useSearch({
    from: "/_auth/slack-link",
  });
  const { data: me } = useMe();
  const orgId = me?.organization?.id ?? "";
  const [status, setStatus] = useState<Status>("linking");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Need either a signed state (legacy) or team_id + slack_user_id (Home tab flow)
    if (!state && !(team_id && slack_user_id)) {
      setStatus("error");
      setError("Missing parameters. Please try again from Slack.");
      return;
    }

    // Wait for org to load
    if (!orgId) return;

    let cancelled = false;

    async function link() {
      try {
        let linkState = state;

        // Home tab flow: call /slack/link to get a signed state first
        if (!linkState && team_id && slack_user_id) {
          const params = new URLSearchParams({ team_id, slack_user_id });
          const stateRes = await apiRequest(`/slack/link?${params}`);
          if (!stateRes.ok) {
            if (cancelled) return;
            const data = await stateRes.json().catch(() => ({}));
            setError(data.detail ?? "Failed to start link. Please try again.");
            setStatus("error");
            return;
          }
          const stateData = await stateRes.json();
          linkState = stateData.state;
        }

        const res = await apiRequest("/slack/link/callback", {
          method: "POST",
          body: JSON.stringify({ state: linkState, org_id: orgId }),
        });

        if (cancelled) return;

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data.detail ?? "Failed to link account. Please try again.");
          setStatus("error");
          return;
        }

        setStatus("success");
      } catch {
        if (!cancelled) {
          setError("Something went wrong. Please try again.");
          setStatus("error");
        }
      }
    }

    void link();
    return () => {
      cancelled = true;
    };
  }, [state, team_id, slack_user_id, orgId]);

  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="max-w-sm w-full text-center space-y-4 p-6">
        {status === "linking" && (
          <>
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            <p className="text-body text-muted-foreground">
              Connecting your Slack account...
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500" />
            <h2 className="text-title font-semibold">Connected!</h2>
            <p className="text-body text-muted-foreground">
              Your Slack account is now linked to Lato. You can close this tab
              and return to Slack.
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="h-8 w-8 mx-auto text-destructive" />
            <h2 className="text-title font-semibold">Connection failed</h2>
            <p className="text-body text-muted-foreground">{error}</p>
          </>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/_auth/slack-link")({
  validateSearch: (search: Record<string, unknown>) => ({
    state: (search.state as string) ?? "",
    team_id: (search.team_id as string) ?? "",
    slack_user_id: (search.slack_user_id as string) ?? "",
  }),
  component: SlackLink,
});
